import { ImageArcGISRest } from 'ol/source';
import type { Projection as OLProjection } from 'ol/proj';
import type { Options as SourceOptions } from 'ol/source/ImageArcGISRest';

import { EsriImageLayerEntryConfig } from '@/api/config/validation-classes/raster-validation-classes/esri-image-layer-entry-config';
import { AbstractGeoViewLayer } from '@/geo/layer/geoview-layers/abstract-geoview-layers';
import { AbstractGeoViewRaster } from '@/geo/layer/geoview-layers/raster/abstract-geoview-raster';
import type { TypeGeoviewLayerConfig, TypeMetadataEsriImage } from '@/api/types/layer-schema-types';
import { CONST_LAYER_TYPES } from '@/api/types/layer-schema-types';

import { EsriUtilities } from '@/geo/layer/geoview-layers/esri-layer-common';
import { GVEsriImage } from '@/geo/layer/gv-layers/raster/gv-esri-image';
import type { ConfigBaseClass, TypeLayerEntryShell } from '@/api/config/validation-classes/config-base-class';
import type { DisplayDateMode } from '@/api/types/map-schema-types';
import type { FetchWithProxyResult } from '@/geo/utils/utilities';

export interface TypeEsriImageLayerConfig extends TypeGeoviewLayerConfig {
  geoviewLayerType: typeof CONST_LAYER_TYPES.ESRI_IMAGE;
  listOfLayerEntryConfig: EsriImageLayerEntryConfig[];
}

/**
 * A class to add an EsriImage layer.
 */
export class EsriImage extends AbstractGeoViewRaster {
  /**
   * Constructs an EsriImage Layer configuration processor.
   *
   * @param layerConfig - The layer configuration
   */
  // The constructor is not useless, it narrows down the accepted parameter type.
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(layerConfig: TypeEsriImageLayerConfig) {
    super(layerConfig);
  }

  // #region OVERRIDES

  /**
   * Overrides the parent class's getter to provide a more specific return type (covariant return).
   *
   * @returns The strongly-typed layer configuration specific to this layer
   */
  override getGeoviewLayerConfig(): TypeEsriImageLayerConfig {
    return super.getGeoviewLayerConfig() as TypeEsriImageLayerConfig;
  }

  /**
   * Overrides the parent class's getter to provide a more specific return type (covariant return).
   *
   * @returns The strongly-typed layer configuration specific to this layer
   */
  override getMetadata(): TypeMetadataEsriImage | undefined {
    return super.getMetadata() as TypeMetadataEsriImage | undefined;
  }

  /**
   * Overrides the way the metadata is fetched.
   *
   * @param abortSignal - Optional {@link AbortSignal} used to cancel the layer creation process
   * @returns A promise that resolves with the fetched metadata and proxy information
   * @throws {LayerServiceMetadataUnableToFetchError} When the metadata fetch fails or contains an error
   */
  protected override onFetchServiceMetadata(abortSignal?: AbortSignal): Promise<FetchWithProxyResult<unknown>> {
    // Redirect using default way of fetching service metadata which is to use the url with f=json parameter
    return this.helperFetchServiceMetadataWithFJson(abortSignal);
  }

  /**
   * Overrides the way a geoview layer config initializes its layer entries.
   *
   * @returns A promise resolved once the layer entries have been initialized
   */
  protected override async onInitLayerEntries(): Promise<TypeGeoviewLayerConfig> {
    // Calls fetchServiceMetadata which delegates to this class's overridden onFetchServiceMetadata (may use a proxy fallback and store the proxyUrl on the instance)
    await this.fetchServiceMetadata();

    // Redirect
    return Promise.resolve(
      EsriImage.createGeoviewLayerConfigSimple(
        this.getGeoviewLayerId(),
        this.getGeoviewLayerName(),
        this.getMetadataAccessPath(),
        this.getGeoviewLayerConfig().isTimeAware
      )
    );
  }

  /**
   * Overrides the way the layer metadata is processed.
   *
   * @param layerConfig - The layer entry configuration to process
   * @param displayDateMode - The display date mode to use for processing time dimensions in the metadata
   * @param mapProjection - Optional map projection
   * @param abortSignal - Optional {@link AbortSignal} used to cancel the layer creation process
   * @returns A promise that the layer entry configuration has gotten its metadata processed
   * @throws {LayerServiceMetadataUnableToFetchError} When the metadata fetch fails or contains an error
   * @throws {LayerServiceMetadataEmptyError} When the layer metadata response is empty
   * @throws {InvalidTimeDimensionError} When the time dimension range cannot be computed
   * @throws {InvalidDateError} When the time dimension contains invalid dates
   * @throws {InvalidProjectionError} When the metadata projection is invalid
   * @throws {RequestTimeoutError} When fetching a missing projection definition exceeds the timeout duration
   * @throws {RequestAbortedError} When fetching a missing projection definition is aborted
   * @throws {ResponseError} When fetching a missing projection definition returns an unsuccessful response
   * @throws {ResponseEmptyError} When a missing projection definition response is empty
   * @throws {NetworkError} When fetching a missing projection definition encounters a network error
   */
  protected override onProcessLayerMetadata(
    layerConfig: EsriImageLayerEntryConfig,
    displayDateMode: DisplayDateMode,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mapProjection?: OLProjection,
    abortSignal?: AbortSignal
  ): Promise<EsriImageLayerEntryConfig> {
    return EsriUtilities.initLayerMetadata(layerConfig, displayDateMode, abortSignal);
  }

  /**
   * Overrides the creation of the GV Layer
   *
   * @param layerConfig - The layer entry configuration
   * @returns The GV Layer
   */
  protected override onCreateGVLayer(layerConfig: EsriImageLayerEntryConfig): GVEsriImage {
    // Create the source
    const source = EsriImage.createEsriImageSource(layerConfig);

    // Create the GV Layer
    const gvLayer = new GVEsriImage(source, layerConfig);

    // Return it
    return gvLayer;
  }

  // #endregion OVERRIDES

  // #region STATIC METHODS

  /**
   * Initializes a GeoView layer configuration for an Esri Image layer.
   *
   * This method creates a basic TypeGeoviewLayerConfig using the provided
   * ID, name, and metadata access path URL. It then initializes the layer entries by calling
   * `initGeoViewLayerEntries`, which may involve fetching metadata or sublayer info.
   *
   * @param geoviewLayerId - A unique identifier for the layer
   * @param geoviewLayerName - The display name of the layer
   * @param metadataAccessPath - The full service URL to the layer endpoint
   * @param isTimeAware - Indicates whether the layer supports time-based filtering
   * @returns A promise that resolves to an initialized GeoView layer configuration with layer entries
   */
  static initGeoviewLayerConfig(
    geoviewLayerId: string,
    geoviewLayerName: string,
    metadataAccessPath: string,
    isTimeAware?: boolean
  ): Promise<TypeGeoviewLayerConfig> {
    // Create the Layer config
    const myLayer = new EsriImage({ geoviewLayerId, geoviewLayerName, metadataAccessPath, isTimeAware } as TypeEsriImageLayerConfig);
    return myLayer.initGeoViewLayerEntries();
  }

  /**
   * Creates a configuration object for a Esri Image layer.
   *
   * This function constructs a `TypeEsriImageLayerConfig` object that describes an Esri Image layer
   * and its associated entry configurations based on the provided parameters.
   *
   * @param geoviewLayerId - A unique identifier for the GeoView layer
   * @param geoviewLayerName - The display name of the GeoView layer
   * @param metadataAccessPath - The URL or path to access metadata
   * @param isTimeAware - Indicates whether the layer supports time-based filtering
   * @returns The constructed configuration object for the Esri Image layer
   */
  static createGeoviewLayerConfigSimple(
    geoviewLayerId: string,
    geoviewLayerName: string | undefined,
    metadataAccessPath: string,
    isTimeAware: boolean | undefined
  ): TypeEsriImageLayerConfig {
    const geoviewLayerConfig: TypeEsriImageLayerConfig = {
      geoviewLayerId,
      geoviewLayerName,
      metadataAccessPath,
      geoviewLayerType: CONST_LAYER_TYPES.ESRI_IMAGE,
      isTimeAware,
      listOfLayerEntryConfig: [],
    };

    let trimmedPath = metadataAccessPath;
    while (trimmedPath.endsWith('/')) {
      trimmedPath = trimmedPath.slice(0, -1);
    }

    geoviewLayerConfig.listOfLayerEntryConfig = [
      new EsriImageLayerEntryConfig({
        geoviewLayerConfig,
        layerId: trimmedPath.split('/').slice(-2, -1)[0],
      }),
    ];

    // Return it
    return geoviewLayerConfig;
  }

  /**
   * Creates a configuration object for a Esri Image layer.
   *
   * This function constructs a `TypeEsriImageLayerConfig` object that describes an Esri Image layer
   * and its associated entry configurations based on the provided parameters.
   *
   * @param geoviewLayerId - A unique identifier for the GeoView layer
   * @param geoviewLayerName - The display name of the GeoView layer
   * @param metadataAccessPath - The URL or path to access metadata
   * @param isTimeAware - Indicates whether the layer supports time-based filtering
   * @param layerEntries - An array of layer entries objects to be included in the configuration
   * @returns The constructed configuration object for the Esri Image layer
   */
  static createGeoviewLayerConfig(
    geoviewLayerId: string,
    geoviewLayerName: string | undefined,
    metadataAccessPath: string,
    isTimeAware: boolean | undefined,
    layerEntries: TypeLayerEntryShell[]
  ): TypeEsriImageLayerConfig {
    const geoviewLayerConfig: TypeEsriImageLayerConfig = {
      geoviewLayerId,
      geoviewLayerName,
      metadataAccessPath,
      geoviewLayerType: CONST_LAYER_TYPES.ESRI_IMAGE,
      isTimeAware,
      listOfLayerEntryConfig: [],
    };

    // Recursively map layer entries
    geoviewLayerConfig.listOfLayerEntryConfig = layerEntries.map((layerEntry) => {
      // TODO: REFACTOR IMPORTANT - This spreading should be rewritten like explained in GeoJSON file. Search id : 59026aa9

      return new EsriImageLayerEntryConfig({
        geoviewLayerConfig: geoviewLayerConfig,
        layerId: `${layerEntry.index || layerEntry.id}`,
        ...(layerEntry.layerName && { layerName: `${layerEntry.layerName}` }),
      });
    });

    // Return it
    return geoviewLayerConfig;
  }

  /**
   * Processes an Esri Image GeoviewLayerConfig and returns a promise
   * that resolves to an array of `ConfigBaseClass` layer entry configurations.
   *
   * This method:
   * 1. Creates a Geoview layer configuration using the provided parameters.
   * 2. Instantiates a layer with that configuration.
   * 3. Processes the layer configuration and returns the result.
   *
   * @param geoviewLayerId - The unique identifier for the GeoView layer
   * @param geoviewLayerName - The display name for the GeoView layer
   * @param url - The URL of the service endpoint
   * @param isTimeAware - Indicates if the layer is time aware
   * @returns A promise that resolves to an array of layer configurations
   * @throws {LayerServiceMetadataUnableToFetchError} When service metadata cannot be fetched
   * @throws {LayerServiceMetadataEmptyError} When the service metadata response is empty
   * @throws {LayerEntryConfigLayerIdNotFoundError} When a configured layer ID is not found
   * @throws {LayerDataAccessPathMandatoryError} When a layer data access path is not configured
   * @throws {LayerEntryConfigEmptyLayerGroupError} When an empty layer group prevents the root layer from being created
   * @throws {LayerEntryConfigUnableToCreateGroupLayerError} When a layer group cannot be created
   * @throws {AggregateError} When multiple layer entries fail to process
   */
  static processGeoviewLayerConfig(
    geoviewLayerId: string,
    geoviewLayerName: string,
    url: string,
    isTimeAware: boolean
  ): Promise<ConfigBaseClass[]> {
    // Create the Layer config
    const layerConfig = EsriImage.createGeoviewLayerConfigSimple(geoviewLayerId, geoviewLayerName, url, isTimeAware);

    // Create the class from geoview-layers package
    const myLayer = new EsriImage(layerConfig);

    // Process it
    return AbstractGeoViewLayer.processConfig(myLayer);
  }

  /**
   * Creates an ImageArcGISRest source from a layer config.
   *
   * @param layerConfig - The configuration for the EsriImage layer
   * @returns A fully configured ImageArcGISRest source
   * @throws {LayerDataAccessPathMandatoryError} When the Data Access Path was undefined, likely because initDataAccessPath wasn't called
   */
  static createEsriImageSource(layerConfig: EsriImageLayerEntryConfig): ImageArcGISRest {
    // Get the source
    const source = layerConfig.getSource();

    // Get raster function and mosaic rule
    const rasterFunction = layerConfig.getInitialRasterFunction();
    const mosaicRule = layerConfig.getInitialMosaicRule();

    // GV The proxy is handled in the setImageLoadFunction callback in GVWMS constructor.
    const sourceOptions: SourceOptions = {
      url: layerConfig.getDataAccessPath(),
      attributions: layerConfig.getAttributions(),
      params: {
        LAYERS: `show:${layerConfig.layerId}`,
        ...(source.transparent !== undefined && { transparent: source.transparent }),
        ...(source.format && { format: source.format }),
        ...(rasterFunction && {
          renderingRule: JSON.stringify({ rasterFunction: layerConfig.getInitialRasterFunction() }),
        }),
        ...(mosaicRule && { mosaicRule: JSON.stringify(mosaicRule) }),
      },
      crossOrigin: source.crossOrigin ?? 'Anonymous',
      projection: layerConfig.getSourceProjectionWithEPSG(),
    };

    // Create the source
    const olSource = new ImageArcGISRest(sourceOptions);

    // Return the source
    return olSource;
  }

  // #endregion STATIC METHODS
}
